/**
 * Lightweight web-notification helpers + scheduled reminder store.
 *
 * Persists pending reminders in localStorage so they survive a page reload
 * (timers are re-scheduled on app load). Works while any tab is open.
 * Full background push (browser closed) requires PWA + Service Worker.
 */
const STORAGE_KEY = "nk_reminders_v1";

function load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch { return []; }
}

function save(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* quota */ }
}

export function isSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function permission() {
  if (!isSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function requestPermission() {
  if (!isSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch { return "denied"; }
}

function show(title, body, tag) {
  if (!isSupported() || Notification.permission !== "granted") return;
  try {
    new Notification(title, {
      body,
      tag: tag || "nk-mode",
      icon: "/favicon.ico",
      badge: "/favicon.ico",
    });
  } catch { /* some browsers throw if invoked from non-user gesture */ }
}

const inflight = new Map(); // key -> setTimeout handle

function schedule(reminder) {
  const delay = new Date(reminder.fireAt).getTime() - Date.now();
  if (delay <= 0) {
    // overdue — pop now if still relevant within 60s of the fire time
    if (delay > -60_000) show(reminder.title, reminder.body, reminder.key);
    return;
  }
  // Cap at 24h (setTimeout max). Re-evaluate on page reload anyway.
  const t = setTimeout(() => {
    show(reminder.title, reminder.body, reminder.key);
    removeReminder(reminder.key);
  }, Math.min(delay, 24 * 60 * 60 * 1000));
  inflight.set(reminder.key, t);
}

/**
 * Schedule a one-time reminder. `key` is unique per mode-slot — calling again
 * with the same key replaces the previous one (useful when the user toggles).
 */
export async function scheduleReminder({ key, title, body, fireAt }) {
  const perm = await requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };
  // Replace any existing reminder under this key
  removeReminder(key);
  const list = load().filter((r) => r.key !== key);
  const reminder = { key, title, body, fireAt };
  list.push(reminder);
  save(list);
  schedule(reminder);
  return { ok: true };
}

export function removeReminder(key) {
  const t = inflight.get(key);
  if (t) { clearTimeout(t); inflight.delete(key); }
  save(load().filter((r) => r.key !== key));
}

export function hasReminder(key) {
  return load().some((r) => r.key === key);
}

/** Called once on app boot to re-arm any reminders saved before a reload. */
export function rehydrate() {
  if (!isSupported()) return;
  const list = load();
  const now = Date.now();
  const fresh = list.filter((r) => new Date(r.fireAt).getTime() > now - 60_000);
  if (fresh.length !== list.length) save(fresh);
  fresh.forEach(schedule);
}
